import { Email } from '../types';

const MAX_SCAN_CHARS = 9000;
const BODY_PREVIEW_CHARS = 6000;

const STRONG_HINT_REGEX =
  /(код\s*(подтверждения|входа|проверки)?|kod\s+weryfikacyjny|одноразов|otp|passcode|one[-\s]?time|two[-\s]?factor|2fa|verification|verify|security\s+code|login\s+code|auth\s+code|email\s+confirmation\s+code|confirmation\s+code|following\s+code|enter\s+the\s+following\s+code|your\s+code|code\s+to\s+confirm|confirm\s+this\s+(?:email|account|identity)|pin)/i;
const PROMO_HINT_REGEX = /(promo\s*code|промокод|coupon|купон|discount|скидк)/i;
const EXPLICIT_CODE_LABEL_REGEX =
  /(код|code|otp|pin|passcode|verification\s*code|promo\s*code|промокод|κωδικός)\b(?:\s+[A-Z0-9][A-Z0-9_-]{1,20}){0,3}\s*(is|:|#|=|-|это|est|είναι)\s*[A-Z0-9]{3,12}\b/i;
const GENERIC_CODE_WITH_VALUE_REGEX =
  /\b(code|код|κωδικός)\b(?:\s+[A-Z0-9][A-Z0-9_-]{1,20}){0,3}\s*(is|:|#|=|-|это|est|είναι)\s*[A-Z0-9]{3,12}\b/i;
const SUBJECT_CODE_CAPTURE_REGEX =
  /\b(code|код|κωδικός)\b(?:\s+[A-Z0-9][A-Z0-9_-]{1,20}){0,3}\s*(is|:|#|=|-|это|est|είναι)\s*([A-Z0-9][A-Z0-9 -]{2,18}[A-Z0-9])\b/i;
const NEGATIVE_CODE_WORD_CONTEXT_REGEX = /(source\s+code|status\s+code|zip\s+code|postal\s+code|area\s+code|qr\s+code)/i;

const DATE_CONTEXT_REGEX =
  /(\bjan\b|\bfeb\b|\bmar\b|\bapr\b|\bmay\b|\bjun\b|\bjul\b|\baug\b|\bsep\b|\bsept\b|\boct\b|\bnov\b|\bdec\b|\bjanuary\b|\bfebruary\b|\bmarch\b|\bapril\b|\bjune\b|\bjuly\b|\baugust\b|\bseptember\b|\boctober\b|\bnovember\b|\bdecember\b|\bянв\b|\bфев\b|\bмар\b|\bапр\b|\bмай\b|\bиюн\b|\bиюл\b|\bавг\b|\bсен\b|\bсент\b|\bокт\b|\bноя\b|\bдек\b|\bгод\b|\byear\b|until|valid until|действует до|до\s+\d{1,2}[./-]\d{1,2})/i;
const ADDRESS_CONTEXT_REGEX =
  /(street|st\.|road|rd\.|avenue|ave\.|blvd|lane|ln\.|drive|dr\.|park|menlo|california|usa|zip|postal|city|state|willow|parkway|suite|офис|улиц|просп|дом|корпус|квартира|индекс)/i;
const URL_PARAM_CONTEXT_REGEX =
  /(https?:\/\/|www\.|[?&](?:expires|expiry|exp|signature|sig|token|auth|session|nonce|hash|ref|uid|id)=|\/verify\/|\/confirm\/|\/reset\/)/i;
const TICKET_CONTEXT_REGEX =
  /(ticket|case|support request|request|issue|incident|closed|re-open|номер тикета|тикет|обращени|заявк)/i;
const CURRENCY_CONTEXT_REGEX =
  /(eur|usd|gbp|uah|rub|kzt|aed|btc|eth|bnb|trx|usdt|€|\$|£|¥|₽|₴|amount|sum|total|withdrawal|deposit|payment|invoice|paid|fee|balance|оплат|сумм|баланс|вывод|депозит|платеж)/i;
const META_EMAIL_UPDATE_CONTEXT_REGEX =
  /(facebook|meta|business manager|menedżer firmy|confirm your email address|email address for|should be updated)/i;

const LINE_CANDIDATE_PATTERN = /\b[A-Z0-9][A-Z0-9 -]{2,18}[A-Z0-9]\b/g;
const DIRECT_PATTERN =
  /(?:otp|passcode|verification(?:\s+code)?|security(?:\s+code)?|login\s+code|auth\s+code|pin|one[-\s]?time(?:\s+password)?|парол[ья]\s*(?:подтверждения|входа)?|код\s*(?:подтверждения|входа|проверки)?|промокод|promo\s*code|(?:\bcode\b\s*(?:is|:|#|=|-)))[^\n\r]{0,40}?([A-Z0-9][A-Z0-9 -]{2,18}[A-Z0-9])/gi;

const normalizeCandidate = (raw: string): string => raw.replace(/[\s-]+/g, '').trim().toUpperCase();

const isPotentialCode = (value: string, allowShortAlphaNum = false): boolean => {
  if (value.length < 4 || value.length > 12) return false;
  if (!/\d/.test(value)) return false;
  if (!/^[A-Z0-9]+$/.test(value)) return false;
  if (/^(\d)\1+$/.test(value)) return false;
  if (/^\d+$/.test(value)) {
    return value.length >= 4 && value.length <= 10;
  }
  const digits = (value.match(/\d/g) || []).length;
  const letters = (value.match(/[A-Z]/g) || []).length;
  if (digits < 2 || letters < 1) return false;
  if (!allowShortAlphaNum && value.length <= 5 && digits < 3) return false;
  return true;
};

const isYearLikeCode = (value: string): boolean => {
  if (!/^\d{4}$/.test(value)) return false;
  const n = Number(value);
  return n >= 1900 && n <= 2099;
};

const isStateZipLikeCode = (value: string): boolean => /^[A-Z]{2}\d{5}$/.test(value);
const isShortNumericCode = (value: string): boolean => /^\d{3,5}$/.test(value);
const isLongNumericLikeUrlParam = (value: string): boolean => /^\d{9,12}$/.test(value);
const isHexColorLike = (value: string): boolean => /^[A-F0-9]{6}$|^[A-F0-9]{3}$|^[A-F0-9]{8}$/.test(value);
const isCurrencyPrefixNumber = (value: string): boolean => /^[A-Z]{3}\d{1,7}$/.test(value);
const hasEnoughDigitsForAuthCode = (value: string): boolean => (value.match(/\d/g) || []).length >= 3;
const isShortBusinessLabel = (value: string): boolean => /^[A-Z]{2,5}\d{1,4}$/.test(value);

const hasUrlLikeContext = (line: string, raw: string): boolean => {
  if (!line || !raw) return false;
  const index = line.toLowerCase().indexOf(raw.toLowerCase());
  if (index < 0) return false;
  const left = line.slice(Math.max(0, index - 28), index);
  const right = line.slice(index + raw.length, Math.min(line.length, index + raw.length + 28));
  const ctx = `${left}${raw}${right}`;
  return URL_PARAM_CONTEXT_REGEX.test(ctx) || /[/?&=#]/.test(left.slice(-3)) || /^[/?&=#]/.test(right);
};

const htmlToText = (html: string): string => {
  if (!html) return '';
  if (!html.includes('<')) return html.replace(/\u00a0/g, ' ');

  // Fast lightweight HTML to text conversion to avoid UI freezes on huge emails.
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

const isMetaBeneficiaryVerificationStatusEmail = (subject: string, text: string): boolean => {
  const subjectText = String(subject || '').trim();
  const source = `${subjectText}\n${String(text || '')}`;
  const looksLikeStatusSubject =
    /^.+?\s+is\s+verified$/i.test(subjectText) ||
    /^we\s+couldn(?:'|’)?t\s+verify\s+.+$/i.test(subjectText);
  if (!looksLikeStatusSubject) return false;
  return /(beneficiary|payer|verification\s+for\s+.+?\s+was\s+successful|weren(?:'|’)?t\s+able\s+to\s+complete\s+verification|go\s+to\s+ads\s+manager|try\s+verification\s+again)/i.test(source);
};

const extractHintedNumericCode = (text: string): string | null => {
  if (!text) return null;

  const hintRegex =
    /(confirmation\s+code|confirm(?:ation)?\s+code|verification\s+code|email\s+confirmation\s+code|following\s+code|enter\s+the\s+following\s+code|your\s+code|код\s+подтверждения|код\s+входа|одноразовый\s+код|kod\s+weryfikacyjny|otp)/ig;
  const numericRegex = /(?:\d[\s-]*){4,8}/g;

  let hintMatch;
  while ((hintMatch = hintRegex.exec(text)) !== null) {
    const start = hintMatch.index + hintMatch[0].length;
    const chunk = text.slice(start, Math.min(text.length, start + 260));
    const numeric = chunk.match(numericRegex)?.[0];
    if (!numeric) continue;
    const code = normalizeCandidate(numeric);
    if (!/^\d{4,8}$/.test(code)) continue;
    if (isYearLikeCode(code)) continue;
    return code;
  }

  return null;
};

interface CandidateScore {
  code: string;
  score: number;
  allowYear?: boolean;
}

export const extractVerificationCode = (email: Pick<Email, 'subject' | 'body'>): string | null => {
  const subject = (email.subject || '').trim();
  const rawBody = String(email.body || '');
  const bodyPreviewRaw = rawBody.includes('<')
    ? htmlToText(rawBody).slice(0, BODY_PREVIEW_CHARS)
    : rawBody.slice(0, BODY_PREVIEW_CHARS);
  const hasAnyHintInRawPreview =
    STRONG_HINT_REGEX.test(bodyPreviewRaw) ||
    PROMO_HINT_REGEX.test(bodyPreviewRaw) ||
    EXPLICIT_CODE_LABEL_REGEX.test(bodyPreviewRaw) ||
    GENERIC_CODE_WITH_VALUE_REGEX.test(bodyPreviewRaw);

  // Fast path: if neither subject nor raw preview have hint words, skip heavy parsing.
  if (
    !STRONG_HINT_REGEX.test(subject) &&
    !PROMO_HINT_REGEX.test(subject) &&
    !EXPLICIT_CODE_LABEL_REGEX.test(subject) &&
    !GENERIC_CODE_WITH_VALUE_REGEX.test(subject) &&
    !hasAnyHintInRawPreview
  ) {
    return null;
  }

  const bodyText = bodyPreviewRaw;
  const text = `${subject}\n${bodyText}`.slice(0, MAX_SCAN_CHARS);

  if (!text) return null;
  if (isMetaBeneficiaryVerificationStatusEmail(subject, bodyText)) return null;

  const lines = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  const candidates = new Map<string, CandidateScore>();
  const subjectHasStrongHint = STRONG_HINT_REGEX.test(subject);
  const subjectHasPromoHint = PROMO_HINT_REGEX.test(subject);
  const subjectHasExplicitLabel = EXPLICIT_CODE_LABEL_REGEX.test(subject);
  const hasPositiveHintAnywhere =
    subjectHasStrongHint ||
    subjectHasPromoHint ||
    subjectHasExplicitLabel ||
    GENERIC_CODE_WITH_VALUE_REGEX.test(subject) ||
    hasAnyHintInRawPreview ||
    STRONG_HINT_REGEX.test(bodyText) ||
    PROMO_HINT_REGEX.test(bodyText) ||
    EXPLICIT_CODE_LABEL_REGEX.test(bodyText) ||
    GENERIC_CODE_WITH_VALUE_REGEX.test(bodyText);

  if (!hasPositiveHintAnywhere) {
    return null;
  }

  const subjectLeadingCode = subject.match(/\b(\d(?:[\s-]*\d){3,7})\s+is\s+your\s+code\b/i)?.[1];
  if (subjectLeadingCode) {
    const normalized = normalizeCandidate(subjectLeadingCode);
    if (/^\d{4,8}$/.test(normalized) && !isYearLikeCode(normalized) && !NEGATIVE_CODE_WORD_CONTEXT_REGEX.test(subject)) {
      return normalized;
    }
  }

  // Ранний и максимально надежный сценарий для писем типа Facebook:
  // "Your email confirmation code" + отдельная строка с цифрами "1 6 9 1 9 1".
  const hintedNumericCode = extractHintedNumericCode(text);
  if (hintedNumericCode) {
    const numericCode = hintedNumericCode;
    if (
      /^\d{4,8}$/.test(numericCode) &&
      !DATE_CONTEXT_REGEX.test(text) &&
      !TICKET_CONTEXT_REGEX.test(text) &&
      !URL_PARAM_CONTEXT_REGEX.test(text)
    ) {
      return numericCode;
    }
  }

  // Быстрый и точный путь: если код явно указан прямо в теме,
  // используем его сразу (для кейсов вроде "Votre code ChatGPT : 212395").
  const subjectDirectMatch = subject.match(SUBJECT_CODE_CAPTURE_REGEX);
  if (subjectDirectMatch?.[3]) {
    const directSubjectCode = normalizeCandidate(subjectDirectMatch[3]);
    if (
      isPotentialCode(directSubjectCode, true) &&
      !isHexColorLike(directSubjectCode) &&
      !isYearLikeCode(directSubjectCode) &&
      !isStateZipLikeCode(directSubjectCode) &&
      !isCurrencyPrefixNumber(directSubjectCode) &&
      !TICKET_CONTEXT_REGEX.test(subject) &&
      !CURRENCY_CONTEXT_REGEX.test(subject) &&
      !NEGATIVE_CODE_WORD_CONTEXT_REGEX.test(subject) &&
      !URL_PARAM_CONTEXT_REGEX.test(subject)
    ) {
      return directSubjectCode;
    }
  }

  const bump = (raw: string, scoreDelta: number, allowShortAlphaNum = false, allowYear = false) => {
    const code = normalizeCandidate(raw);
    if (!isPotentialCode(code, allowShortAlphaNum)) return;
    const prev = candidates.get(code);
    if (prev) {
      prev.score += scoreDelta;
      prev.allowYear = Boolean(prev.allowYear || allowYear);
      return;
    }
    candidates.set(code, { code, score: scoreDelta, allowYear });
  };

  const directRegex = new RegExp(DIRECT_PATTERN);
  let directMatch: RegExpExecArray | null = null;
  while ((directMatch = directRegex.exec(text)) !== null) {
    if (!directMatch[1]) continue;
    const normalized = normalizeCandidate(directMatch[1]);
    const directContext = (directMatch[0] || '').toLowerCase();

    // Не считаем год из дат кодом (типичный false-positive, например "2026").
    if (isYearLikeCode(normalized) && DATE_CONTEXT_REGEX.test(directContext)) {
      continue;
    }
    if (isStateZipLikeCode(normalized) && ADDRESS_CONTEXT_REGEX.test(directContext)) {
      continue;
    }
    if (URL_PARAM_CONTEXT_REGEX.test(directContext) && (isLongNumericLikeUrlParam(normalized) || /^[A-F0-9]{12,}$/i.test(normalized))) {
      continue;
    }
    if (isHexColorLike(normalized) && !/(code|код|otp|pin|passcode|verification|промокод|promo\s*code)\s*[:#-]\s*[a-f0-9]{3,8}/i.test(directContext)) {
      continue;
    }
    if (NEGATIVE_CODE_WORD_CONTEXT_REGEX.test(directContext)) {
      continue;
    }
    if (TICKET_CONTEXT_REGEX.test(directContext) && !/(code|код|otp|pin|passcode|verification|промокод|promo\s*code)\s*[:#-]/i.test(directContext)) {
      continue;
    }
    if (CURRENCY_CONTEXT_REGEX.test(directContext) && (isCurrencyPrefixNumber(normalized) || /^\d{4,}$/.test(normalized))) {
      continue;
    }

    bump(directMatch[1], 12, true, /(code|код|otp|pin)\s*[:#=-]\s*\d{4}/i.test(directContext));
  }

  lines.forEach((line, idx) => {
    const prevLine = lines[idx - 1] || '';
    const nextLine = lines[idx + 1] || '';
    const lineHasStrongHint = STRONG_HINT_REGEX.test(line);
    const lineHasPromoHint = PROMO_HINT_REGEX.test(line);
    const lineHasUrl = /https?:\/\/|www\./i.test(line);
    const lineHasEmail = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(line);
    const lineHasAddressContext = ADDRESS_CONTEXT_REGEX.test(line);
    const lineHasTicketContext = TICKET_CONTEXT_REGEX.test(line);
    const lineHasCurrencyContext = CURRENCY_CONTEXT_REGEX.test(line);
    const lineHasMetaEmailUpdateContext = META_EMAIL_UPDATE_CONTEXT_REGEX.test(`${prevLine} ${line} ${nextLine}`);
    const lineHasExplicitCodeLabel = EXPLICIT_CODE_LABEL_REGEX.test(line);
    const lineHasGenericCodeWord = /\bcode\b/i.test(line) && !NEGATIVE_CODE_WORD_CONTEXT_REGEX.test(line);
    const lineHasUrlParamContext = URL_PARAM_CONTEXT_REGEX.test(line);
    const lineHasNegativeCodeWord = NEGATIVE_CODE_WORD_CONTEXT_REGEX.test(line);
    const prevHasStrongHint = STRONG_HINT_REGEX.test(prevLine);
    const nextHasStrongHint = STRONG_HINT_REGEX.test(nextLine);
    const prevHasExplicitCodeLabel = EXPLICIT_CODE_LABEL_REGEX.test(prevLine);
    const nextHasExplicitCodeLabel = EXPLICIT_CODE_LABEL_REGEX.test(nextLine);
    const prevHasGenericCodeWord = /\bcode\b/i.test(prevLine) && !NEGATIVE_CODE_WORD_CONTEXT_REGEX.test(prevLine);
    const nextHasGenericCodeWord = /\bcode\b/i.test(nextLine) && !NEGATIVE_CODE_WORD_CONTEXT_REGEX.test(nextLine);

    const lineRegex = new RegExp(LINE_CANDIDATE_PATTERN);
    let lineMatch: RegExpExecArray | null = null;
    while ((lineMatch = lineRegex.exec(line)) !== null) {
      const raw = lineMatch[0];
      const code = normalizeCandidate(raw);
      const allowShortAlphaNum =
        lineHasExplicitCodeLabel ||
        lineHasStrongHint ||
        lineHasPromoHint ||
        prevHasExplicitCodeLabel ||
        nextHasExplicitCodeLabel ||
        prevHasStrongHint ||
        nextHasStrongHint;
      if (!isPotentialCode(code, allowShortAlphaNum)) continue;

      // Жестко режем адресные значения: "1601", "CA94025" и т.п.
      if (lineHasAddressContext && !lineHasExplicitCodeLabel) {
        if (isStateZipLikeCode(code) || isShortNumericCode(code)) continue;
      }
      // Не считаем query/path значения из URL кодом (expires/signature/token/...).
      if ((lineHasUrl || lineHasUrlParamContext) && !lineHasExplicitCodeLabel) {
        if (hasUrlLikeContext(line, raw)) continue;
        if (isLongNumericLikeUrlParam(code)) continue;
      }
      // HEX-цвета из html/css (например F1F4F7) не считаем кодами.
      if (isHexColorLike(code) && !lineHasExplicitCodeLabel) {
        continue;
      }
      if (lineHasNegativeCodeWord && !lineHasExplicitCodeLabel) {
        continue;
      }
      // Номер тикета/кейса не является кодом.
      if (lineHasTicketContext && !lineHasExplicitCodeLabel) {
        if (/^\d{4,10}$/.test(code) || /^[A-Z0-9]{5,12}$/.test(code)) continue;
      }
      // Валютные суммы и префиксы (EUR13, USD1000...) не являются кодом.
      if (lineHasCurrencyContext && !lineHasExplicitCodeLabel) {
        if (isCurrencyPrefixNumber(code) || /^\d{4,}$/.test(code)) continue;
      }
      // Meta/Facebook sometimes says "confirm your email address for PER 258 should be updated".
      // PER 258 is a business/account label, not an OTP.
      if (lineHasMetaEmailUpdateContext && !lineHasExplicitCodeLabel) {
        if (isShortBusinessLabel(code)) continue;
      }
      if (lineHasGenericCodeWord && !lineHasExplicitCodeLabel) {
        const isNumericOtp = /^\d{4,8}$/.test(code);
        const isAlnumOtp = code.length >= 5 && code.length <= 10 && hasEnoughDigitsForAuthCode(code);
        if (!isNumericOtp && !isAlnumOtp) continue;
      }

      let score = 0;

      if (lineHasStrongHint) score += 10;
      if (subjectHasStrongHint) score += 5;
      if (lineHasPromoHint) score += 8;
      if (subjectHasPromoHint) score += 3;
      if (lineHasExplicitCodeLabel) score += 8;
      if (subjectHasExplicitLabel) score += 3;
      if (lineHasGenericCodeWord) score += 4;
      if (prevHasStrongHint || nextHasStrongHint) score += 6;
      if (prevHasExplicitCodeLabel || nextHasExplicitCodeLabel) score += 5;
      if (prevHasGenericCodeWord || nextHasGenericCodeWord) score += 3;
      if (/^\d{4,8}$/.test(code)) score += 2;
      // Частый шаблон писем (в т.ч. Facebook): фраза "confirmation code"
      // на строке выше и сам цифровой код отдельной строкой.
      if (/^\d{4,8}$/.test(code) && (prevHasStrongHint || nextHasStrongHint)) score += 3;
      if (/^[A-Z0-9]{6}$/.test(code)) score += 2;
      if (/[A-Z]/.test(code) && /\d/.test(code)) score += 2;
      if (idx < 15) score += 1;
      if (/действ|expired|expire|минут|min|valid/i.test(line)) score += 2;
      if (lineHasUrl) score -= 2;
      if (lineHasEmail) score -= 1;
      if (lineHasUrlParamContext) score -= 4;
      if (lineHasTicketContext) score -= 4;
      if (lineHasCurrencyContext) score -= 4;
      if (lineHasNegativeCodeWord) score -= 5;
      if (/^\d{10,12}$/.test(code) && !lineHasStrongHint && !subjectHasStrongHint && !lineHasPromoHint && !subjectHasPromoHint) score -= 5;
      if (isYearLikeCode(code)) {
        score -= 6;
        if (DATE_CONTEXT_REGEX.test(line)) score -= 6;
        // Год может быть кодом только при очень явном контексте "code: 1234".
        if (/(code|код|otp|pin)\s*[:#-]\s*\d{4}/i.test(line)) score += 8;
      }
      if (isStateZipLikeCode(code)) {
        score -= 8;
        if (lineHasAddressContext) score -= 8;
        // Редкий кейс, когда код реально формата AA99999 и явно помечен как код.
        if (/(code|код|otp|pin)\s*[:#-]\s*[A-Z]{2}\d{5}/i.test(line)) score += 10;
      }
      if (isShortNumericCode(code) && lineHasAddressContext && !lineHasExplicitCodeLabel) {
        score -= 10;
      }
      if (lineHasAddressContext && !lineHasStrongHint && !subjectHasStrongHint && !lineHasPromoHint && !subjectHasPromoHint) {
        score -= 3;
      }

      bump(code, score, allowShortAlphaNum);
    }
  });

  const best = Array.from(candidates.values()).filter(candidate => {
    if (!isYearLikeCode(candidate.code)) return true;
    return Boolean(candidate.allowYear);
  }).sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const aHasLetters = /[A-Z]/.test(a.code);
    const bHasLetters = /[A-Z]/.test(b.code);
    if (aHasLetters !== bHasLetters) return bHasLetters ? 1 : -1;
    return Math.abs(6 - a.code.length) - Math.abs(6 - b.code.length);
  })[0];
  if (!best) return null;
  if (best.score < 10) return null;

  return best.code;
};
